import {
  type CapFilter,
  type CapKind,
  type CapMessage,
  type GrantedCap,
  type JsonValue,
  readCapMessage,
  sendCapMessage,
} from "@gelatos/protocol";
import type { ObjectHandle, ObjectSource } from "../workspace/objects.js";
import type { PtyOpener, PtySession } from "../sprite/pty.js";
import {
  createObject,
  objectMeta,
  type ObjectSnapshot,
  snapshot,
  type WorkspaceDoc,
} from "../workspace/schema.js";

/** What a `collection` capability lets its holder see (§5.3). */
export type CollectionScope =
  | { kind: "private"; owner: string }
  | { kind: "granted"; objects: readonly string[] };

export type PowerboxRequest = {
  windowId: string;
  appId: string;
  kind: CapKind;
  filter: CapFilter | undefined;
};

/** Returns the object the user chose, or null if they closed the picker. */
export type PowerboxPicker = (request: PowerboxRequest) => Promise<string | null>;

type Served = {
  id: string;
  /**
   * What this capability points at: an object id, a Sprite name, or "" for a
   * private store.
   */
  target: string;
  kind: CapKind;
  revoke: () => void;
  /**
   * For a `stream`, the live session behind it. The shell's terminal widget
   * renders this; the app never sees it, and cannot reach it without holding
   * the capability the id belongs to.
   */
  session?: PtySession;
};

/**
 * Mints capabilities and takes them away again (§5.3, §5.4).
 *
 * Three rules the rest of the shell depends on:
 *
 * 1. **An app is mounted with exactly one capability** — a `collection` scoped
 *    to its own private store. Everything else it must be *given*.
 * 2. **Every port is booked against a window.** Closing the window closes them
 *    all, which is what makes revocation real rather than advisory.
 * 3. **A grant is the user's act of choosing** (§5.4). `requestCapability` goes
 *    to the picker; there is no separate permission prompt anywhere, and if you
 *    find yourself writing one you have misread the model.
 */
export class CapabilityBroker {
  readonly #ws: WorkspaceDoc;
  readonly #objects: ObjectSource;
  readonly #pick: PowerboxPicker;
  readonly #byWindow = new Map<string, Served[]>();
  readonly #byId = new Map<string, Served>();
  #counter = 0;

  readonly #openPty: PtyOpener | null;

  constructor(deps: {
    ws: WorkspaceDoc;
    objects: ObjectSource;
    pick: PowerboxPicker;
    /**
     * How to start a terminal in a Sprite. Absent means this workspace has no
     * Sprite yet, and `exec` is simply not on offer — which is the honest
     * answer, not a capability that fails when called.
     */
    openPty?: PtyOpener;
  }) {
    this.#ws = deps.ws;
    this.#objects = deps.objects;
    this.#pick = deps.pick;
    this.#openPty = deps.openPty ?? null;
  }

  /** The one capability an app is mounted with. */
  privateCollection(windowId: string, appId: string): GrantedCap {
    return this.#collection(windowId, appId, { kind: "private", owner: appId }, `${appId} store`);
  }

  /** Hand a specific object to a window. Used by the powerbox and by drops. */
  async objectCapability(windowId: string, objectId: string): Promise<GrantedCap | null> {
    const meta = objectMeta(this.#ws, objectId);
    if (meta === null) return null;
    const handle = await this.#objects.acquire(objectId);
    return this.#object(windowId, objectId, handle, meta);
  }

  /**
   * Run something in one named Sprite (§10.3, resolved).
   *
   * Scoped to a Sprite by the grant, never to "the workspace": "run in my
   * environment" and "run in that agent's sandbox" must not be the same
   * authority, so `target` names which.
   */
  execCapability(windowId: string, target: string): GrantedCap | null {
    const open = this.#openPty;
    if (open === null) return null;
    const id = `${windowId}/${++this.#counter}`;
    const channel = new MessageChannel();
    const stop = serve(channel.port1, (method, params, reply) => {
      if (method !== "spawn") throw new Error(`unknown exec method ${method}`);
      const spec = record(params);
      const granted = this.#stream(windowId, target, open, {
        ...(typeof spec["command"] === "string" ? { command: spec["command"] } : {}),
        ...(typeof spec["cols"] === "number" ? { cols: spec["cols"] } : {}),
        ...(typeof spec["rows"] === "number" ? { rows: spec["rows"] } : {}),
      });
      reply({ id: granted.id, label: granted.label ?? target }, granted.port);
    });
    this.#register(windowId, { id, target, kind: "exec", revoke: stop });
    return { id, kind: "exec", port: channel.port2, label: target };
  }

  /**
   * The live session behind a `stream`, for the shell's own terminal widget.
   *
   * Scoped to the window that holds the capability. A `Surface` naming a
   * capability id it was not granted resolves to nothing — which is what stops
   * a rendered node from being a way to reach past the capability model (§5.1).
   */
  streamSession(windowId: string, capabilityId: string): PtySession | null {
    const served = this.#byId.get(capabilityId);
    if (served === undefined || served.kind !== "stream") return null;
    if (!(this.#byWindow.get(windowId) ?? []).includes(served)) return null;
    return served.session ?? null;
  }

  /** What object a capability points at, so a drop can re-grant it elsewhere. */
  targetOf(capabilityId: string): string | null {
    const served = this.#byId.get(capabilityId);
    return served === undefined || served.target === "" ? null : served.target;
  }

  /**
   * An app asked for something (§5.2 `requestCapability`).
   *
   * The picker is shown; whatever the user chooses is the grant. A null answer
   * means they closed it, which the app sees as `capabilityDenied` — the same
   * message either way, because "the user declined to choose" and "you may not
   * have this" are the same fact from the app's side.
   */
  async request(request: PowerboxRequest): Promise<GrantedCap | null> {
    if (request.kind === "stream") return null; // only ever handed out by `exec`
    // Whatever the picker returns is named in the namespace of the kind asked
    // for: an object id for object/collection, a Sprite name for exec.
    const chosen = await this.#pick(request);
    if (chosen === null) return null;
    if (request.kind === "exec") return this.execCapability(request.windowId, chosen);
    const objectId = chosen;

    if (request.kind === "collection") {
      const meta = objectMeta(this.#ws, objectId);
      return this.#collection(
        request.windowId,
        request.appId,
        { kind: "granted", objects: [objectId] },
        meta?.name ?? "selection",
      );
    }
    return this.objectCapability(request.windowId, objectId);
  }

  /** Close every port minted for a window. This *is* revocation. */
  revokeWindow(windowId: string): void {
    const served = this.#byWindow.get(windowId);
    if (served === undefined) return;
    this.#byWindow.delete(windowId);
    for (const entry of served) {
      this.#byId.delete(entry.id);
      entry.revoke();
    }
  }

  destroy(): void {
    for (const windowId of [...this.#byWindow.keys()]) this.revokeWindow(windowId);
  }

  #register(windowId: string, entry: Served): void {
    const list = this.#byWindow.get(windowId);
    if (list === undefined) this.#byWindow.set(windowId, [entry]);
    else list.push(entry);
    this.#byId.set(entry.id, entry);
  }

  #collection(
    windowId: string,
    appId: string,
    scope: CollectionScope,
    label: string,
  ): GrantedCap {
    const id = `${windowId}/${++this.#counter}`;
    const channel = new MessageChannel();
    const revoke = serveCollection(channel.port1, {
      ws: this.#ws,
      scope,
      appId,
      openObject: (objectId) => this.objectCapability(windowId, objectId),
    });
    this.#register(windowId, { id, target: "", kind: "collection", revoke });
    return { id, kind: "collection", port: channel.port2, label };
  }

  #stream(
    windowId: string,
    target: string,
    open: PtyOpener,
    spec: { command?: string; cols?: number; rows?: number },
  ): GrantedCap {
    const id = `${windowId}/${++this.#counter}`;
    const channel = new MessageChannel();
    const session = open(spec);
    const stop = serveStream(channel.port1, session);
    this.#register(windowId, {
      id,
      target,
      kind: "stream",
      session,
      revoke: () => {
        stop();
        // Revoking a terminal kills it. A stream that outlived its capability
        // would be a process nobody can see and nobody can stop — and, in a
        // Sprite, one that keeps the machine awake and billing (§4.6.6).
        session.close();
      },
    });
    return { id, kind: "stream", port: channel.port2, label: target };
  }

  #object(
    windowId: string,
    objectId: string,
    handle: ObjectHandle,
    meta: ObjectSnapshot,
  ): GrantedCap {
    const id = `${windowId}/${++this.#counter}`;
    const channel = new MessageChannel();
    const stop = serveObject(channel.port1, handle, meta);
    this.#register(windowId, {
      id,
      target: objectId,
      kind: "object",
      revoke: () => {
        stop();
        this.#objects.release(objectId);
      },
    });
    return {
      id,
      kind: "object",
      port: channel.port2,
      objectType: meta.type,
      label: meta.name,
    };
  }
}

/* ------------------------------------------------------------- endpoints */

type Reply = (value: JsonValue | undefined, port?: MessagePort) => void;

function serve(
  port: MessagePort,
  handle: (method: string, params: JsonValue | undefined, reply: Reply) => Promise<void> | void,
): () => void {
  let closed = false;

  port.onmessage = (event: MessageEvent): void => {
    if (closed) return;
    let message: CapMessage;
    try {
      message = readCapMessage(event);
    } catch {
      return;
    }
    if (message.t !== "call") return;
    const id = message.id;
    const reply: Reply = (value, transferred) => {
      if (closed) return;
      sendCapMessage(port, {
        t: "result",
        id,
        ...(value === undefined ? {} : { value }),
        ...(transferred === undefined ? {} : { port: transferred }),
      });
    };
    void (async () => {
      try {
        await handle(message.method, message.params, reply);
      } catch (error) {
        if (closed) return;
        sendCapMessage(port, {
          t: "error",
          id,
          message: error instanceof Error ? error.message : String(error),
        });
      }
    })();
  };
  port.start();

  return () => {
    if (closed) return;
    closed = true;
    // Say so before going away, so a holder mid-call fails loudly rather than
    // waiting forever on a promise that will never settle.
    try {
      sendCapMessage(port, { t: "event", event: "revoked" });
    } catch {
      // Already gone.
    }
    port.onmessage = null;
    port.close();
  };
}

function record(params: JsonValue | undefined): Record<string, JsonValue> {
  return typeof params === "object" && params !== null && !Array.isArray(params)
    ? (params as Record<string, JsonValue>)
    : {};
}

function serveObject(port: MessagePort, handle: ObjectHandle, meta: ObjectSnapshot): () => void {
  let unobserve: (() => void) | null = null;
  // What this holder last wrote. Echoing its own write back would fight with
  // whatever it is showing, exactly as it would for a rendered input.
  let lastWritten: string | null = null;

  const stop = serve(port, (method, params, reply) => {
    switch (method) {
      case "get":
        reply({ content: handle.content(), type: meta.type, name: meta.name });
        return;
      case "set": {
        const content = record(params)["content"];
        if (typeof content !== "string") throw new Error("set requires a string `content`");
        lastWritten = content;
        handle.setContent(content);
        reply(undefined);
        return;
      }
      case "attrs":
        reply({ attrs: meta.attrs });
        return;
      case "subscribe": {
        unobserve?.();
        unobserve = handle.observe((content) => {
          if (content === lastWritten) return;
          lastWritten = null;
          sendCapMessage(port, { t: "event", event: "change", payload: { content } });
        });
        reply(undefined);
        return;
      }
      default:
        throw new Error(`unknown object method ${method}`);
    }
  });

  return () => {
    unobserve?.();
    unobserve = null;
    stop();
  };
}

function serveStream(port: MessagePort, session: PtySession): () => void {
  let unsubscribe: (() => void) | null = null;
  let unclose: (() => void) | null = null;

  const stop = serve(port, (method, params, reply) => {
    switch (method) {
      case "write": {
        const data = record(params)["data"];
        if (typeof data !== "string") throw new Error("write requires base64 `data`");
        session.write(data);
        reply(undefined);
        return;
      }
      case "onData": {
        unsubscribe?.();
        unsubscribe = session.subscribe((data) => {
          sendCapMessage(port, { t: "event", event: "data", payload: { data } });
        });
        unclose?.();
        unclose = session.onClose((reason) => {
          sendCapMessage(port, { t: "event", event: "close", payload: { reason } });
        });
        reply(undefined);
        return;
      }
      case "resize": {
        const fields = record(params);
        const cols = fields["cols"];
        const rows = fields["rows"];
        if (typeof cols !== "number" || typeof rows !== "number") {
          throw new Error("resize requires numeric `cols` and `rows`");
        }
        session.resize(cols, rows);
        reply(undefined);
        return;
      }
      case "close":
        session.close();
        reply(undefined);
        return;
      default:
        throw new Error(`unknown stream method ${method}`);
    }
  });

  return () => {
    unsubscribe?.();
    unclose?.();
    unsubscribe = null;
    unclose = null;
    stop();
  };
}

function serveCollection(
  port: MessagePort,
  deps: {
    ws: WorkspaceDoc;
    scope: CollectionScope;
    appId: string;
    openObject: (objectId: string) => Promise<GrantedCap | null>;
  },
): () => void {
  let unobserve: (() => void) | null = null;

  const visible = (): ObjectSnapshot[] => {
    const scope = deps.scope;
    const all = snapshot(deps.ws).objects;
    if (scope.kind === "granted") {
      const allowed = new Set(scope.objects);
      return all.filter((object) => allowed.has(object.id));
    }
    // A private store contains what this app made, and nothing else. This is
    // the line that stops one app enumerating another's objects.
    return all.filter((object) => object.attrs["owner"] === scope.owner);
  };

  const stop = serve(port, async (method, params, reply) => {
    switch (method) {
      case "list": {
        const wanted = record(params)["objectTypes"];
        const types = Array.isArray(wanted)
          ? new Set(wanted.filter((t): t is string => typeof t === "string"))
          : null;
        reply({
          objects: visible()
            .filter((object) => types === null || types.has(object.type))
            .map((object) => ({
              id: object.id,
              type: object.type,
              name: object.name,
              created: object.created,
            })),
        });
        return;
      }
      case "create": {
        if (deps.scope.kind !== "private") {
          throw new Error("this collection is a selection, not a store");
        }
        const fields = record(params);
        const type = typeof fields["type"] === "string" ? fields["type"] : "application/octet-stream";
        const name = typeof fields["name"] === "string" ? fields["name"] : "Untitled";
        reply({
          id: createObject(deps.ws, { type, name, owner: deps.appId, now: Date.now() }),
        });
        return;
      }
      case "open": {
        const id = record(params)["id"];
        if (typeof id !== "string") throw new Error("open requires an `id`");
        if (!visible().some((object) => object.id === id)) {
          // Not in scope: the same answer as "does not exist", deliberately.
          // Distinguishing the two would leak the existence of objects this
          // capability does not cover.
          throw new Error(`no such object in this collection`);
        }
        const granted = await deps.openObject(id);
        if (granted === null) throw new Error("could not open object");
        // The *capability* id, not the object id: that is what an ObjectRef
        // carries and what a drop resolves against. An object id would be a
        // name to look up, which is exactly what capabilities replace.
        reply(
          { id: granted.id, type: granted.objectType ?? "", name: granted.label ?? "" },
          granted.port,
        );
        return;
      }
      case "subscribe": {
        unobserve?.();
        const listener = (): void => {
          sendCapMessage(port, { t: "event", event: "change" });
        };
        deps.ws.objects.observeDeep(listener);
        unobserve = () => deps.ws.objects.unobserveDeep(listener);
        reply(undefined);
        return;
      }
      default:
        throw new Error(`unknown collection method ${method}`);
    }
  });

  return () => {
    unobserve?.();
    unobserve = null;
    stop();
  };
}
